import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Stack,
  Grid,
} from "@mui/material";
import StyledTooltip from "../../utils/styledToolTip";
import { formatDecimal } from "../../utils/numberFormat";

export default function MorbityResults({ projects, forPdf=false }) {
  /* ----------------------------------------------------
   * CORES
   * -------------------------------------------------- */
  const CONDITION_COLORS = [
    "#CFE6EF", // Coronary heart disease
    "#BDEBD9", // Stroke
    "#F3D7A1", // Dementia
    "#EDD1C6", // Type II diabetes
    "#E9DDF3", // Depression
    "#E04A8B", // Breast cancer (female)
    "#12C7C3", // Colon cancer (male)
  ];
  const colorForCondition = (i) => CONDITION_COLORS[i % CONDITION_COLORS.length];

  const PROJECT_PALETTE = ["#7C3AED", "#22C55E", "#3B82F6"];
  const colorForProject = (i) => PROJECT_PALETTE[i % PROJECT_PALETTE.length];

  /* ----------------------------------------------------
   * HELPERS
   * -------------------------------------------------- */
  const firstNumber = (obj, keys = []) => {
    for (const k of keys) {
      if (obj && Object.prototype.hasOwnProperty.call(obj, k)) {
        const v = obj[k];
        if (typeof v === "number" && Number.isFinite(v)) return v;
      }
    }
    return null;
  };

  const asDecimal = (pct) => (pct > 1 ? pct / 100 : pct); // 0..1

  function formatNumber(value, decimals = 2) {
    if (typeof value !== "number" || !Number.isFinite(value)) return "--";
    return formatDecimal(value, decimals);
  }

  /* ----------------------------------------------------
   * MÉTRICAS (definimos chaves prováveis)
   * -------------------------------------------------- */
  const METRICS = [
    {
      key: "chd",
      label: "Coronary heart disease",
      unit: "years",
      decimals: 2,
      // valores diretos em anos, se existirem
      yearsKeys: [
        "coronaryHeartReductionInYearsDisability",
        "coronaryHeartReductionInYearsLivedWithDisability",
        "coronaryHeartYearsReduction",
        "coronaryHeartYLDYearsReduction",
      ],
      // percentagem (0..1 ou 0..100)
      percentKeys: [
        "coronaryHeartReductionInRiskOfDisease",
        "coronaryHeartReductionInRiskDisease",
      ],
      // baseline em anos
      baselineKeys: [
        "coronaryHeartBaselineYearsLivedWithDisabilities",
        "coronaryHeartBaselineYears",
        "coronaryHeartYLDBaselineYears",
      ],
    },
    {
      key: "stroke",
      label: "Stroke",
      unit: "years",
      decimals: 2,
      yearsKeys: [
        "strokeReductionInYearsDisability",
        "strokeReductionInYearsLivedWithDisability",
        "strokeYearsReduction",
        "strokeYLDYearsReduction",
      ],
      percentKeys: [
        "strokeReductionInRiskOfDisease",
        "strokeReductionInRiskDisease",
      ],
      baselineKeys: [
        "strokeBaselineYearsLivedWithDisabilities",
        "strokeBaselineYears",
        "strokeYLDBaselineYears",
      ],
    },
    {
      key: "dementia",
      label: "Dementia",
      unit: "years",
      decimals: 2,
      yearsKeys: [
        "dementiaReductionInYearsDisability",
        "dementiaReductionInYearsLivedWithDisability",
        "dementiaYearsReduction",
        "dementiaYLDYearsReduction",
      ],
      percentKeys: [
        "dementiaReductionInRiskOfDisease",
        "dementiaReductionInRiskDisease",
      ],
      baselineKeys: [
        "dementiaBaselineYearsLivedWithDisabilities",
        "dementiaBaselineYears",
        "dementiaYLDBaselineYears",
      ],
    },
    {
      key: "t2d",
      label: "Type II diabetes",
      unit: "years",
      decimals: 2,
      yearsKeys: [
        "typeIIDiabetesReductionInYearsDisability",
        "typeIIDiabetesReductionInYearsLivedWithDisability",
        "typeIIDiabetesYearsReduction",
        "typeIIDiabetesYLDYearsReduction",
      ],
      percentKeys: [
        "typeIIDiabetesReductionInRiskOfDisease",
        "typeIIDiabetesReductionInRiskDisease",
      ],
      baselineKeys: [
        "typeIIDiabetesBaselineYearsLivedWithDisabilities",
        "typeIIDiabetesBaselineYears",
        "typeIIDiabetesYLDBaselineYears",
      ],
    },
    {
      key: "depression",
      label: "Depression",
      unit: "years",
      decimals: 2,
      yearsKeys: [
        "depressionReductionInYearsDisability",
        "depressionReductionInYearsLivedWithDisability",
        "depressionYearsReduction",
        "depressionYLDYearsReduction",
      ],
      percentKeys: [
        "depressionReductionInRiskOfDisease",
        "depressionReductionInRiskDisease",
      ],
      baselineKeys: [
        "depressionBaselineYearsLivedWithDisabilities",
        "depressionBaselineYears",
        "depressionYLDBaselineYears",
      ],
    },
    {
      key: "breastCancerF",
      label: "Breast cancer (female)",
      unit: "years",
      decimals: 2,
      yearsKeys: [
        "breastCancerReductionInYearsDisability",
        "breastCancerReductionInYearsLivedWithDisability",
        "breastCancerYearsReduction",
        "breastCancerYLDYearsReduction",
      ],
      percentKeys: [
        "breastCancerReductionInRiskOfDisease",
        "breastCancerReductionInRiskDisease",
      ],
      baselineKeys: [
        "breastCancerBaselineYearsLivedWithDisabilities",
        "breastCancerBaselineYears",
        "breastCancerYLDBaselineYears",
      ],
    },
    {
      key: "colonCancerM",
      label: "Colon cancer (male)",
      unit: "years",
      decimals: 2,
      yearsKeys: [
        "colonCancerReductionInYearsDisability",
        "colonCancerReductionInYearsLivedWithDisability",
        "colonCancerYearsReduction",
        "colonCancerYLDYearsReduction",
      ],
      percentKeys: [
        "colonCancerReductionInRiskOfDisease",
        "colonCancerReductionInRiskDisease",
      ],
      baselineKeys: [
        "colonCancerBaselineYearsLivedWithDisabilities",
        "colonCancerBaselineYears",
        "colonCancerYLDBaselineYears",
      ],
    },
  ];

  /* ----------------------------------------------------
   * Valores por métrica
   * -------------------------------------------------- */
  // percentagem da redução (decimal 0..1). Se o teu dado já vem 0..1, ok; se vier 0..100, converte.
  const valuePercent = (project, m) => {
    const info = project?.projectInfo ?? {};
    const pct = firstNumber(info, m.percentKeys);
    return pct != null ? Math.max(0, asDecimal(pct)) : 0;
  };

  // anos reduzidos (direto ou baseline * percent)
  const valueYears = (project, m) => {
    const info = project?.projectInfo ?? {};
    // 1) direto em anos
    const vYears = firstNumber(info, m.yearsKeys);
    if (vYears != null) return Math.max(0, vYears);
    // 2) cálculo
    const pct = firstNumber(info, m.percentKeys);
    const base = firstNumber(info, m.baselineKeys);
    if (pct != null && base != null) {
      return Math.max(0, asDecimal(pct) * base);
    }
    return 0;
  };

  /* ----------------------------------------------------
   * STACK PARTS — barras por projeto
   *  - widthPct calculado por percentagem (não anos)
   *  - tooltips e total em ANOS
   * -------------------------------------------------- */
  function getStackParts(project) {
    // valores brutos
    const raw = METRICS.map((m, i) => {
      const pct = valuePercent(project, m); // 0..1
      const yrs = valueYears(project, m);
      return {
        key: m.key,
        label: m.label,
        percent: pct,
        years: yrs,
        color: colorForCondition(i),
        unit: "years",
        decimals: m.decimals,
      };
    });

    const totalPercent = raw.reduce((s, r) => s + r.percent, 0); // 0..N (somatório dos decimais)
    const totalYears = raw.reduce((s, r) => s + r.years, 0);

    // normalizar para que a barra empilhada (interna) some 100% de largura
    const parts = raw.map((r) => ({
      ...r,
      widthPct: totalPercent > 0 ? (r.percent / totalPercent) * 100 : 0, // <-- percent‑based
    }));

    return { totalPercent, totalYears, parts };
  }

  /* ----------------------------------------------------
   * RENDER
   * -------------------------------------------------- */
  return (
    <Box sx={{ mt: 5, ...(forPdf && { mt: 0, mb: 0, pageBreakBefore: 'always' }) }}>

      <Typography variant="h6" align="center" sx={{ fontWeight: 700, mb: 3, mt: 1 }}>
        Reduction in Years Lived with Disabilities
        <StyledTooltip
          title="Years lived with disability (YLD) represents the number of years of healthy life lost due to disability or disease."
          arrow
        >
          <span
            className="icon"
            style={{ width: 28, height: 20, backgroundRepeat: "no-repeat", backgroundPositionX: "right" }}
          ></span>
        </StyledTooltip>
      </Typography>

      {/* --- Tabela (valores em ANOS) --- */}
      <TableContainer sx={{ width: "100%" }}>
        <Table stickyHeader sx={{ mt: 1.5, tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow
              sx={{
                "& > .MuiTableCell-root": {
                  borderBottom: "1px solid #e0e0e0 !important",
                },
              }}
            >
              <TableCell sx={{ width: "100%" }}>
                <Typography variant="subtitle1" color="text.secondary" align="left" sx={{ fontWeight: 600, px: 0 }}>
                  Condition
                </Typography>
              </TableCell>

              {projects.map((project) => (
                <TableCell
                  key={project.id ?? project.projectName}
                  align="right"
                  sx={{ width: 160, maxWidth: 160, px: 0 }}
                >
                  <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ fontWeight: 600, px: 1 }}>
                    {project.projectName}
                  </Typography>
                </TableCell>
              ))}

              <TableCell sx={{ width: 80, maxWidth: 80 }}>
                <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ fontWeight: 600, px: 1 }}>
                  Unit
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {METRICS.map((m) => (
              <TableRow key={m.key} aria-label={m.key}>
                {/* Condição */}
                <TableCell
                  sx={{
                    width: `calc(100% - ${projects.length * 160}px)`,
                    py: 1.25,
                    borderBottom: "1px solid #e0e0e0 !important",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {m.label}
                  </Typography>
                </TableCell>

                {/* Valores por projeto (EM ANOS) */}
                {projects.map((project) => {
                  const valYears = valueYears(project, m);
                  const isNumber = typeof valYears === "number" && Number.isFinite(valYears);
                  return (
                    <TableCell
                      key={(project.id ?? project.projectName) + ":" + m.key}
                      align="right"
                      sx={{
                        width: 160,
                        minWidth: 160,
                        maxWidth: 160,
                        borderBottom: "1px solid #e0e0e0 !important",
                      }}
                    >
                      <Typography
                        variant="body2"
                        align="center"
                        noWrap
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                        title={isNumber ? `${valYears} years` : ""}
                      >
                        {isNumber ? formatNumber(valYears, m.decimals) : "--"}
                      </Typography>
                    </TableCell>
                  );
                })}

                {/* Unidade */}
                <TableCell
                  sx={{
                    width: 80,
                    maxWidth: 80,
                    px: 0,
                    borderBottom: "1px solid #e0e0e0 !important",
                  }}
                >
                  <Typography variant="body2" align="center" noWrap>
                    years
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* --- Legenda --- */}
      <Box sx={{ mt: 10, mb: 4, ...(forPdf && { mt: 2, mb: 0 }) }}>
        <Stack direction="row" spacing={0.2} alignItems="flex-start" flexWrap="wrap">
          {METRICS.map((m, i) => (
            <Stack key={"legend:" + m.key} direction="column" spacing={0.5} alignItems="center" sx={{ minWidth: 80 }}>
              <Box
                sx={{
                  width: "100%",
                  height: 6,
                  borderRadius: 0,
                  backgroundColor: colorForCondition(i),
                }}
              />
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ textAlign: "center", whiteSpace: "nowrap", px: 1.5 }}
              >
                {m.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* --- Barra empilhada por projeto (largura por %; hover mostra ANOS) --- */}
      <Box sx={{ width: "100%", mt: 3 }}>
        <Grid container spacing={forPdf ? 0.55 : 3}>
          {(() => {
            // Total por projeto em percentagem (para largura externa relativa)
            const totalsPct = projects.map((proj) => getStackParts(proj).totalPercent || 0);
            const maxTotalPct = Math.max(0, ...totalsPct);

            return projects.map((project, idxProj) => {
              const { totalPercent, totalYears, parts } = getStackParts(project);
              const projectColor = colorForProject(idxProj);

              // largura da barra do projeto baseada no TOTAL EM % (não anos)
              const proportionalWidthPct = maxTotalPct > 0 ? (totalPercent / maxTotalPct) * 100 : 0;
              const barWidthPct = Math.min(Math.max(proportionalWidthPct, 6), 100); // mínimo visual

              return (
                <Grid item xs={12} key={"stack:" + (project.id ?? project.projectName)}>
                  {/* Nome do projeto */}
                  <Typography
                    variant="subtitle1"
                    sx={{ color: projectColor, mb: 0, lineHeight: 1.4 }}
                  >
                    {project.projectName}
                  </Typography>

                  {/* Barra empilhada */}
                  <Stack direction="row" alignItems="center" spacing={0} sx={{ width: "100%", mt: 0 }}>
                    <Box
                      sx={{
                        width: `${barWidthPct}%`,
                        height: 16,
                        borderRadius: 0,
                        overflow: "hidden",
                        display: "flex",
                        transition: "width .25s ease",
                      }}
                      // tooltip do contêiner mostra TOTAL em ANOS
                      title={`Total: ${formatNumber(totalYears, 2)} years`}
                    >
                      {parts.map((p) => (
                        <Box
                          key={"seg:" + p.key}
                          sx={{
                            width: `${p.widthPct}%`, // percent‑based dentro da barra
                            backgroundColor: p.color,
                            height: "100%",
                            minWidth: 2,
                          }}
                          // tooltip do segmento mostra ANOS do segmento
                          title={`${p.label}: ${formatNumber(p.years, p.decimals)} years`}
                        />
                      ))}
                    </Box>
                  </Stack>
                </Grid>
              );
            });
          })()}
        </Grid>
      </Box>
    </Box>
  );
}
